import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Tag, Button, Modal, Form, Input, DatePicker, Select, message, Spin, Badge, Empty, Space, List, Popconfirm } from 'antd';
import { CalendarOutlined, PlusOutlined, DeleteOutlined, EditOutlined, CheckCircleOutlined, ClockCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;

const statusColors = { scheduled: 'blue', released: 'green', cancelled: 'red' };
const platformColors = { All: 'purple', YouTube: 'red', Spotify: 'green', 'iTunes/Apple': 'default', JioSaavn: 'blue' };

export default function ReleaseCalendar({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(dayjs());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/calendar', { params: { month: month.format('YYYY-MM') } });
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, [month]);

  const handleAdd = async (values) => {
    try {
      await api.post('/performance/calendar', {
        ...values,
        release_date: values.release_date.format('YYYY-MM-DD')
      });
      message.success('Release scheduled!');
      setAddModal(false);
      form.resetFields();
      fetchEvents();
    } catch (err) { message.error('Failed'); }
  };

  const handleEdit = async (values) => {
    try {
      await api.put(`/performance/calendar/${editModal.id}`, {
        ...values,
        release_date: values.release_date.format('YYYY-MM-DD')
      });
      message.success('Updated!');
      setEditModal(null);
      fetchEvents();
    } catch (err) { message.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/performance/calendar/${id}`);
      message.success('Deleted!');
      fetchEvents();
    } catch (err) { message.error('Failed'); }
  };

  // Group events by date
  const daysInMonth = month.daysInMonth();
  const calendarDays = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = month.date(d).format('YYYY-MM-DD');
    const dayEvents = events.filter(e => e.release_date === dateStr);
    calendarDays.push({ date: d, dateStr, events: dayEvents, isToday: dateStr === dayjs().format('YYYY-MM-DD') });
  }

  const upcomingReleases = events.filter(e => e.status === 'scheduled' && e.release_date >= dayjs().format('YYYY-MM-DD'));

  const formFields = (
    <>
      <Form.Item name="title" label="Song Title" rules={[{ required: true }]}><Input /></Form.Item>
      <Form.Item name="artist" label="Artist/Singer"><Input /></Form.Item>
      <Form.Item name="release_date" label="Release Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
      <Form.Item name="platform" label="Platform" initialValue="All">
        <Select options={[{ value: 'All' }, { value: 'YouTube' }, { value: 'Spotify' }, { value: 'iTunes/Apple' }, { value: 'JioSaavn' }]} />
      </Form.Item>
      <Form.Item name="status" label="Status" initialValue="scheduled">
        <Select options={[{ value: 'scheduled', label: 'Scheduled' }, { value: 'released', label: 'Released' }, { value: 'cancelled', label: 'Cancelled' }]} />
      </Form.Item>
      <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
    </>
  );

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col><Title level={3}><CalendarOutlined /> Release Calendar</Title></Col>
        <Col>
          <Space>
            <DatePicker picker="month" value={month} onChange={v => setMonth(v || dayjs())} />
            {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>Add Release</Button>}
            <Button icon={<PrinterOutlined />} onClick={() => {
              const w = window.open('', '_blank');
              w.document.write(`<!DOCTYPE html><html><head><title>Release Calendar - ${month.format('MMMM YYYY')}</title>
                <style>body{font-family:Arial;padding:30px}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px}
                .header h1{margin:0;font-size:22px}table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}th{background:#f0f5ff}
                .footer{text-align:center;color:#999;font-size:12px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}</style></head><body>
                <div class="header"><h1>Release Calendar</h1><p>Bainsla Music | ${month.format('MMMM YYYY')}</p></div>
                <p>Scheduled: <b>${events.filter(e => e.status==='scheduled').length}</b> | Released: <b>${events.filter(e => e.status==='released').length}</b> | Cancelled: <b>${events.filter(e => e.status==='cancelled').length}</b></p>
                <table><tr><th>#</th><th>Title</th><th>Artist</th><th>Release Date</th><th>Platform</th><th>Status</th><th>Notes</th></tr>
                ${events.map((e, i) => `<tr><td>${i+1}</td><td>${e.title}</td><td>${e.artist||'-'}</td><td>${dayjs(e.release_date).format('DD MMM YYYY')}</td><td>${e.platform}</td><td>${e.status}</td><td>${e.notes||'-'}</td></tr>`).join('')}
                </table><div class="footer">Bainsla Music Data Dashboard | ${dayjs().format('DD MMM YYYY, hh:mm A')}</div></body></html>`);
              w.document.close(); setTimeout(() => w.print(), 500);
            }}>PDF</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Calendar grid */}
        <Col xs={24} md={16}>
          {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
            <Card size="small">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontWeight: 600, padding: 6, background: '#f0f5ff', borderRadius: 4, fontSize: 12 }}>{d}</div>
                ))}
                {/* Empty cells for start day offset */}
                {Array.from({ length: month.startOf('month').day() }, (_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {calendarDays.map(day => (
                  <div key={day.date} style={{
                    minHeight: 80, padding: 4, border: '1px solid #f0f0f0', borderRadius: 6,
                    background: day.isToday ? '#e6f7ff' : day.events.length > 0 ? '#f6ffed' : '#fff'
                  }}>
                    <div style={{ fontWeight: day.isToday ? 700 : 400, fontSize: 12, color: day.isToday ? '#1890ff' : '#333' }}>
                      {day.date}
                    </div>
                    {day.events.map((ev, i) => (
                      <Tag
                        key={i}
                        color={statusColors[ev.status]}
                        style={{ fontSize: 10, marginTop: 2, cursor: 'pointer', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                        onClick={() => {
                          if (isAdmin) {
                            setEditModal(ev);
                            editForm.setFieldsValue({
                              ...ev,
                              release_date: dayjs(ev.release_date)
                            });
                          }
                        }}
                      >
                        {ev.title}
                      </Tag>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </Col>

        {/* Upcoming releases sidebar */}
        <Col xs={24} md={8}>
          <Card title={<span><ClockCircleOutlined /> Upcoming Releases</span>} size="small">
            {upcomingReleases.length === 0 ? (
              <Empty description="No upcoming releases" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                dataSource={upcomingReleases}
                renderItem={ev => (
                  <List.Item
                    actions={isAdmin ? [
                      <EditOutlined onClick={() => { setEditModal(ev); editForm.setFieldsValue({ ...ev, release_date: dayjs(ev.release_date) }); }} />,
                      <Popconfirm title="Delete?" onConfirm={() => handleDelete(ev.id)}><DeleteOutlined style={{ color: 'red' }} /></Popconfirm>
                    ] : []}
                  >
                    <List.Item.Meta
                      title={<Text strong style={{ fontSize: 13 }}>{ev.title}</Text>}
                      description={
                        <Space size={4}>
                          <Tag color={statusColors[ev.status]}>{ev.status}</Tag>
                          <Tag color={platformColors[ev.platform] || 'default'}>{ev.platform}</Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(ev.release_date).format('DD MMM')}</Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>

          {/* This month stats */}
          <Card title="This Month" size="small" style={{ marginTop: 16 }}>
            <Row gutter={8}>
              <Col span={8}><Tag color="blue" style={{ display: 'block', textAlign: 'center', padding: '4px 0' }}>{events.filter(e => e.status === 'scheduled').length}<br />Scheduled</Tag></Col>
              <Col span={8}><Tag color="green" style={{ display: 'block', textAlign: 'center', padding: '4px 0' }}>{events.filter(e => e.status === 'released').length}<br />Released</Tag></Col>
              <Col span={8}><Tag color="red" style={{ display: 'block', textAlign: 'center', padding: '4px 0' }}>{events.filter(e => e.status === 'cancelled').length}<br />Cancelled</Tag></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Add Modal */}
      <Modal title="Schedule New Release" open={addModal} onCancel={() => setAddModal(false)} footer={null}>
        <Form form={form} onFinish={handleAdd} layout="vertical">{formFields}<Button type="primary" htmlType="submit" block>Schedule Release</Button></Form>
      </Modal>

      {/* Edit Modal */}
      <Modal title="Edit Release" open={!!editModal} onCancel={() => setEditModal(null)} footer={null}>
        <Form form={editForm} onFinish={handleEdit} layout="vertical">{formFields}<Button type="primary" htmlType="submit" block>Update</Button></Form>
      </Modal>
    </div>
  );
}
